const VISITOR_STORAGE_KEY = "sfast.website.visitor-id";
const SESSION_STORAGE_KEY = "sfast.website.session-id";
const ANALYTICS_ENDPOINT = "/api/public/website-events";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export interface AnalyticsRuntime {
  crypto?: { randomUUID?: () => string };
  localStorage?: StorageLike;
  sessionStorage?: StorageLike;
  Blob?: typeof Blob;
  navigator?: { sendBeacon?: (url: string, data?: BodyInit | null) => boolean };
  fetch?: typeof fetch;
  location?: { pathname?: string; hash?: string };
  document?: { title?: string; referrer?: string };
  addEventListener?: (type: string, listener: () => void) => void;
}

export interface WebsiteEventInput {
  pagePath?: string;
  pageTitle?: string;
  target?: string;
  referrer?: string;
  metadata?: Record<string, unknown>;
}

export interface WebsiteEvent extends WebsiteEventInput {
  type: string;
  visitorId: string;
  sessionId: string;
  pagePath: string;
  pageTitle: string;
  target: string;
  referrer: string;
  metadata: Record<string, unknown>;
}

export interface WebsiteTracker {
  start: () => void;
  track: (type: string, input?: WebsiteEventInput) => boolean;
  trackPageView: () => boolean;
  visitorId: string;
  sessionId: string;
}

function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function identifier(storage: StorageLike | undefined, key: string, runtime: AnalyticsRuntime): string {
  try {
    const existing = storage?.getItem(key);
    if (existing) return existing;
    const next = runtime.crypto?.randomUUID?.() || fallbackUuid();
    storage?.setItem(key, next);
    return next;
  } catch {
    return runtime.crypto?.randomUUID?.() || fallbackUuid();
  }
}

function pagePath(location: AnalyticsRuntime["location"]): string {
  return `${location?.pathname || "/"}${location?.hash || ""}`.slice(0, 512);
}

function cleanText(value: unknown, maximum: number): string {
  return String(value || "").trim().slice(0, maximum);
}

export function createWebsiteTracker(runtime: AnalyticsRuntime = globalThis, endpoint = ANALYTICS_ENDPOINT): WebsiteTracker {
  const document = runtime.document;
  const navigator = runtime.navigator || {};
  const fetcher = runtime.fetch?.bind(runtime);
  const visitorId = identifier(runtime.localStorage, VISITOR_STORAGE_KEY, runtime);
  const sessionId = identifier(runtime.sessionStorage, SESSION_STORAGE_KEY, runtime);
  let started = false;
  let lastPagePath = "";

  function send(event: WebsiteEvent): boolean {
    const body = JSON.stringify({ events: [event] });
    try {
      const BlobClass = runtime.Blob || globalThis.Blob;
      const payload = BlobClass ? new BlobClass([body], { type: "application/json" }) : body;
      if (navigator.sendBeacon?.(endpoint, payload)) return true;
    } catch {
      // Beacon is best effort; keepalive fetch below is the fallback.
    }
    if (!fetcher) return false;
    void fetcher(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin"
    }).catch(() => {});
    return true;
  }

  function track(type: string, input: WebsiteEventInput = {}): boolean {
    const location = runtime.location || {};
    return send({
      type,
      visitorId,
      sessionId,
      pagePath: cleanText(input.pagePath || pagePath(location), 512) || "/",
      pageTitle: cleanText(input.pageTitle || document?.title, 200),
      target: cleanText(input.target, 512),
      referrer: cleanText(input.referrer ?? document?.referrer, 1024),
      metadata: input.metadata && typeof input.metadata === "object" ? input.metadata : {}
    });
  }

  function trackPageView(): boolean {
    const currentPath = pagePath(runtime.location);
    if (!currentPath || currentPath === lastPagePath) return false;
    lastPagePath = currentPath;
    return track("page_view", { pagePath: currentPath });
  }

  function start(): void {
    if (started) return;
    started = true;
    trackPageView();
    runtime.addEventListener?.("hashchange", trackPageView);
  }

  return { start, track, trackPageView, visitorId, sessionId };
}

let defaultTracker: WebsiteTracker | null = null;

export function startWebsiteAnalytics(): WebsiteTracker {
  if (!defaultTracker) defaultTracker = createWebsiteTracker();
  defaultTracker.start();
  return defaultTracker;
}

export function trackWebsiteEvent(type: string, input?: WebsiteEventInput): boolean {
  return (defaultTracker || startWebsiteAnalytics()).track(type, input);
}
